import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {InvoicesListsOrdersDataGridCellKeys} from "../../../../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../../../packages/data-grid";
import IconButton from "../../../../../../../../components/base/button/icon_button";
import {OpenInNew} from "@mui/icons-material";
import {formatMoney} from "../../../../../../../../../core/services/utils/utils";
import PayMultiInvoicesDialog from "../../../../../../../../components/dialogs/pay-multiple-invoices";
import PartnerDataGrid from "../../../../../../../../containers/data-grid";
import {ReactComponent as Passed} from "../../../../../../../../../assets/images/validations/passed.svg";
import {ReactComponent as Failed} from "../../../../../../../../../assets/images/validations/failed.svg";
import {Checkbox} from "../../../../../../../../components/base/checkbox/checkbox";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";
import {CircularProgress, SvgIcon} from "@mui/material";
import {ReactComponent as Printer} from '../../../../../../../../../assets/images/printer.svg'
import useAccessLevels from "../../../../../../../../hooks/use-access-levels";
import {useAppSelector} from "../../../../../../../../redux";
import PartnerIndustryRules from "../../../../../../../../../core/services/partner-industry-rules";
import {usePartnerInformation} from "../../../../../../../../hooks/use-partner-information";
import CacheService from "../../../../../../../../../core/services/cache/cache_service";


const initPayMultiInvoices = {open: false, list: []};

const dataGridActions = {
    pay: 'pay',
}

const InvoicesListViewOrdersTable = ({
                                         searchData,
                                         exportToCsv,
                                         data,
                                         allowPayments,
                                         fetchOrders,
                                         search,
                                         dataGridSaveConfig,
                                         navigateToInformationView,
                                         navigateToPrintView,
                                         fetchingOrdersCustomer,
                                     }) => {
    const [payMultipleInvoicesDialog, setPayMultipleInvoicesDialog] = useState(initPayMultiInvoices);
    const [selectedOrders, setSelectedOrders] = useState([]);
    /**@type {React.MutableRefObject<DataGridController>}*/
    const dataGridApi = useRef();
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const partnerInformation = usePartnerInformation();
    const partnerId = useMemo(() => partnerInformation?.id ?? CacheService.getOperatorPartnerId(), [partnerInformation?.id]);

    const canPayMultipleInvoices = useMemo(() =>
            (searchData.customers?.length === 1 || !!defaultCustomer?.id)
            && allowPayments
            && operatorAccessLevels.packages.sales.invoiceMultiPayments
        , [searchData.customers?.length, defaultCustomer?.id, allowPayments, operatorAccessLevels.packages.sales.invoiceMultiPayments])

    /**
     * With each change in the customers of the state:
     * - Resets the selection of the data-grid.
     */
    useEffect(() => {
        if (!dataGridApi.current)
            return;
        dataGridApi.current.resetSelection();
    }, [searchData.customers, dataGridApi, defaultCustomer?.id])


    /**
     * Fetches all the orders given the available length of the orders.
     * @param pageLength
     * @return {Promise<*[]>}
     */
    const getAllOrders = useCallback(async (pageLength) => {
        const _paginationInfo = {
            length: pageLength,
            pageSize: 20,
            currentPage: 1,
        };
        let entries = [];
        while (pageLength > entries.length) {
            const res = await fetchOrders({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                continue;
            }
            if (!res?.data?.resultList?.length) {
                break;
            }
            entries.push(...res.data.resultList);
            if (!isMounted())
                return entries;
            _paginationInfo.currentPage++;
        }
        return entries;
    }, [fetchOrders, isMounted, searchData])

    /**
     * Opens the multi invoice payment dialog with the selected invoices.
     */
    const openMultiInvoicePaymentDialog = useCallback((list) => {
        setPayMultipleInvoicesDialog((prevState) => ({
            ...prevState,
            open: true,
            list: list,
        }))
    }, [])

    /**
     * Closes the multi payment invoices dialog and searches for the entries again.
     */
    const closeMultiInvoicePaymentDialog = useCallback(() => {
        search(searchData).then();
        dataGridApi.current.resetSelection();
        setPayMultipleInvoicesDialog(initPayMultiInvoices)
    }, [search, searchData])

    /**
     * Handles the invocation of the data grid actions.
     *
     * @param {DataGridFooterAction} action
     * @param {DataGridSelectionEntry[]} selectedRows
     * @param {DataGridSelectionEntry[]} excludedRows
     * @param {boolean} allRowsSelected
     * @param {DataGridPagination} pagination
     */
    const onActionInvoked = useCallback(async (action, selectedRows, excludedRows, allRowsSelected, pagination) => {
        switch (action.name) {
            case dataGridActions.pay: {
                if (!allRowsSelected) {
                    // not all rows are selected, therefore, the selectedRows list is not empty
                    return openMultiInvoicePaymentDialog(selectedRows.map(e => e.data));
                }
                if (pagination.pageSize >= pagination.length) {
                    // all the items are within a single page, so we have the data in hand
                    return openMultiInvoicePaymentDialog(data);
                }
                openMultiInvoicePaymentDialog(await getAllOrders(pagination.length));
                break;
            }
            default:
                return;
        }
    }, [getAllOrders, data, openMultiInvoicePaymentDialog])

    /**
     * Sets the [selectedOrders] state value with each change of the row selection state of the data-grid.
     * @param _
     * @param {DataGridRowSelectionInfo} current
     */
    const onRowSelectionInfoChanged = useCallback(async (_, {allRowsSelected, selectedRows}) => {
        if (!allRowsSelected) {
            return setSelectedOrders(selectedRows.map(e => e.data));
        }
        if (searchData.paginationInfo.pageSize >= searchData.paginationInfo.length) {
            return setSelectedOrders(data);
        }
        setSelectedOrders(await getAllOrders(searchData.paginationInfo.length));
    }, [data, getAllOrders, searchData.paginationInfo.length, searchData.paginationInfo.pageSize])

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...(operatorAccessLevels.packages.sales.invoiceInformation ? [
                {
                    name: InvoicesListsOrdersDataGridCellKeys.navigate,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    pinned: true,
                    sortable: false,
                    pinnedToggleable: false,
                    resizable: false,
                    visibilityToggleable: false,
                    width: 0,
                },
                ...(operatorAccessLevels.packages.bizPrint.printInvoice ? [
                    {
                        name: InvoicesListsOrdersDataGridCellKeys.print,
                        alignment: DataGridColumnAlignments.center,
                        type: DataGridColumnTypes.element,
                        pinned: true,
                        sortable: false,
                        pinnedToggleable: false,
                        resizable: false,
                        visibilityToggleable: false,
                        width: 0,
                    },
                ] : []),
            ] : []),
            {
                title: canPayMultipleInvoices ? "Pay" : "Status",
                name: InvoicesListsOrdersDataGridCellKeys.status,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                pinned: true,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                width: canPayMultipleInvoices ? 40 : 60,
            },
            {
                title: "Invoice No",
                name: InvoicesListsOrdersDataGridCellKeys.invoiceNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: true,
            },
            {
                title: "Customer",
                name: InvoicesListsOrdersDataGridCellKeys.customer,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Invoice Date",
                name: InvoicesListsOrdersDataGridCellKeys.invoiceDate,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.date,
            },
            {
                title: "Customer PO",
                name: InvoicesListsOrdersDataGridCellKeys.customerPO,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Sales Person",
                name: InvoicesListsOrdersDataGridCellKeys.salespersonNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Total",
                name: InvoicesListsOrdersDataGridCellKeys.totalOrdered,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 120,
            },
            {
                title: "Balance",
                name: InvoicesListsOrdersDataGridCellKeys.balance,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 120,
            },
        ], [canPayMultipleInvoices, operatorAccessLevels.packages.sales.invoiceInformation, operatorAccessLevels.packages.bizPrint.printInvoice])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
    });


    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return data?.map(order => ({
            key: order.id,
            data: order,
            cells: {
                [InvoicesListsOrdersDataGridCellKeys.status]: ({rowSelected, toggleRowSelection}) =>
                    !PartnerIndustryRules.effectiveIsNeededToPay(order, partnerId)
                        ? <Passed/>
                        : !canPayMultipleInvoices
                            ? <Failed/>
                            : <Checkbox
                                labelClassName={'invoice-selection-checkbox'}
                                checked={rowSelected}
                                value={rowSelected}
                                onChange={() => toggleRowSelection()}
                            />,
                [InvoicesListsOrdersDataGridCellKeys.navigate]:
                    <IconButton
                        color={'secondary'}
                        disabled={fetchingOrdersCustomer.includes(order.id)}
                        onClick={() => navigateToInformationView(order.customer, order.id)}

                    >
                        {
                            fetchingOrdersCustomer.includes(order.id)
                                ? <CircularProgress size={20} color={'secondary'}/>
                                : <OpenInNew className='icon normal'/>
                        }
                    </IconButton>,
                [InvoicesListsOrdersDataGridCellKeys.print]:
                    <IconButton
                        color={'paper.secondary'}
                        disabled={fetchingOrdersCustomer.includes(order.id)}
                        onClick={() => navigateToPrintView(order.customer, order.id)}
                    >
                        {
                            fetchingOrdersCustomer.includes(order.id)
                                ? <CircularProgress size={20} color={'secondary'}/>
                                : <SvgIcon className='icon medium' component={Printer}/>
                        }

                    </IconButton>,
                [InvoicesListsOrdersDataGridCellKeys.invoiceNo]: order.invoiceNo,
                [InvoicesListsOrdersDataGridCellKeys.customer]: order.customer?.name,
                [InvoicesListsOrdersDataGridCellKeys.invoiceDate]: order.invoiceDate,
                [InvoicesListsOrdersDataGridCellKeys.customerPO]: order.customerPO,
                [InvoicesListsOrdersDataGridCellKeys.salespersonNo]: order.salespersonNo,
                [InvoicesListsOrdersDataGridCellKeys.totalOrdered]: order.total,
                [InvoicesListsOrdersDataGridCellKeys.balance]: order.balance,
            }
        }))
    }, [data, canPayMultipleInvoices, partnerId, fetchingOrdersCustomer, navigateToInformationView, navigateToPrintView])

    const dataGridFooterActions = useMemo(() =>
        /**@type {DataGridFooterAction[]}*/
        [{
            name: dataGridActions.pay,
            title: () => {
                const amount = selectedOrders
                    ?.map(e => PartnerIndustryRules.effectiveRemainingBalance(e, partnerId))
                    ?.reduce((a, b) => a + b, 0) ?? 0;
                return `Pay ${selectedOrders?.length ?? 0} invoice${selectedOrders?.length === 1 ? '' : 's'} (${formatMoney(amount)})`
            },
            visibilityConstraints: {
                minSelectedRows: 1,
            }
        }], [selectedOrders, partnerId])

    return (
        <>
            <PartnerDataGrid
                ref={dataGridApi}
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                columns={dataGridColumns}
                rows={dataGridRows}
                state={{
                    pagination: searchData.paginationInfo,
                    sortBy: searchData.orderBy,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                footerActions={dataGridFooterActions}
                hideFooterActions={!canPayMultipleInvoices}
                getExportInfo={exportToCsv}
                onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                onActionInvoked={onActionInvoked}
                onRowSelectionInfoChanged={onRowSelectionInfoChanged}
            />
            {
                operatorAccessLevels.packages.sales.invoiceMultiPayments &&
                <PayMultiInvoicesDialog
                    selectedInvoices={payMultipleInvoicesDialog.list}
                    customerId={(searchData.customers?.length === 1 ? searchData.customers[0] : defaultCustomer)?.id ?? null}
                    open={payMultipleInvoicesDialog.open}
                    close={closeMultiInvoicePaymentDialog}
                />
            }
        </>
    );
}

export default InvoicesListViewOrdersTable;
